import { useState } from 'react';

interface SolidVisualProps {
  solidId: string;
  name: string;
}

export default function SolidVisual({ solidId, name }: SolidVisualProps) {
  const [rotation, setRotation] = useState(0);

  const renderSolid = () => {
    switch (solidId) {
      case 'rectangular-prism':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Front face */}
              <div
                className="absolute bg-teal-400/80 border-2 border-teal-600"
                style={{
                  width: '120px',
                  height: '80px',
                  transform: 'translateZ(40px)'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-teal-900">Front</div>
              </div>
              {/* Back face */}
              <div
                className="absolute bg-teal-300/60 border-2 border-teal-500"
                style={{
                  width: '120px',
                  height: '80px',
                  transform: 'translateZ(-40px) rotateY(180deg)'
                }}
              />
              {/* Top face */}
              <div
                className="absolute bg-teal-500/70 border-2 border-teal-700"
                style={{
                  width: '120px',
                  height: '80px',
                  transform: 'rotateX(90deg) translateZ(40px)'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-white">Top</div>
              </div>
              {/* Bottom face */}
              <div
                className="absolute bg-teal-300/50 border-2 border-teal-500"
                style={{
                  width: '120px',
                  height: '80px',
                  transform: 'rotateX(-90deg) translateZ(40px)'
                }}
              />
              {/* Right face */}
              <div
                className="absolute bg-teal-400/70 border-2 border-teal-600"
                style={{
                  width: '80px',
                  height: '80px',
                  transform: 'rotateY(90deg) translateZ(60px)'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-teal-900">Right</div>
              </div>
              {/* Left face */}
              <div
                className="absolute bg-teal-300/60 border-2 border-teal-500"
                style={{
                  width: '80px',
                  height: '80px',
                  transform: 'rotateY(-90deg) translateZ(60px)'
                }}
              />
            </div>
          </div>
        );

      case 'triangular-prism':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Front triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(20, 184, 166, 0.8)',
                  transform: 'translateZ(50px)'
                }}
              />
              {/* Back triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(20, 184, 166, 0.5)',
                  transform: 'translateZ(-50px)'
                }}
              />
              {/* Bottom face */}
              <div
                className="absolute bg-teal-400/70 border-2 border-teal-600"
                style={{
                  width: '120px',
                  height: '100px',
                  transform: 'rotateX(90deg) translateY(50px)'
                }}
              />
              {/* Left face */}
              <div
                className="absolute bg-teal-500/70 border-2 border-teal-700"
                style={{
                  width: '100px',
                  height: '120px',
                  transform: 'rotateY(-60deg) translateX(-30px) translateZ(25px)'
                }}
              />
              {/* Right face */}
              <div
                className="absolute bg-teal-500/70 border-2 border-teal-700"
                style={{
                  width: '100px',
                  height: '120px',
                  transform: 'rotateY(60deg) translateX(30px) translateZ(25px)'
                }}
              />
            </div>
          </div>
        );

      case 'square-pyramid':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Base */}
              <div
                className="absolute bg-violet-400/80 border-2 border-violet-600"
                style={{
                  width: '120px',
                  height: '120px',
                  transform: 'translateY(60px) rotateX(90deg)'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-violet-900">Base</div>
              </div>
              {/* Front triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(139, 92, 246, 0.8)',
                  transform: 'translateZ(60px) translateY(-10px)'
                }}
              />
              {/* Back triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(139, 92, 246, 0.5)',
                  transform: 'translateZ(-60px) rotateY(180deg) translateY(-10px)'
                }}
              />
              {/* Left triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(139, 92, 246, 0.7)',
                  transform: 'rotateY(-90deg) translateZ(60px) translateY(-10px)'
                }}
              />
              {/* Right triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(139, 92, 246, 0.7)',
                  transform: 'rotateY(90deg) translateZ(60px) translateY(-10px)'
                }}
              />
            </div>
          </div>
        );

      case 'triangular-pyramid':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Base triangle */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '70px solid transparent',
                  borderRight: '70px solid transparent',
                  borderBottom: '120px solid rgba(236, 72, 153, 0.8)',
                  transform: 'translateY(40px) rotateX(90deg)'
                }}
              />
              {/* Front face */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '70px solid transparent',
                  borderRight: '70px solid transparent',
                  borderBottom: '100px solid rgba(236, 72, 153, 0.7)',
                  transform: 'translateZ(40px) translateY(-10px)'
                }}
              />
              {/* Left face */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(236, 72, 153, 0.6)',
                  transform: 'rotateY(-120deg) translateZ(40px) translateY(-10px)'
                }}
              />
              {/* Right face */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '100px solid rgba(236, 72, 153, 0.6)',
                  transform: 'rotateY(120deg) translateZ(40px) translateY(-10px)'
                }}
              />
            </div>
          </div>
        );

      case 'cylinder':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Top ellipse */}
              <div
                className="absolute bg-amber-400/80 border-2 border-amber-600 rounded-full"
                style={{
                  width: '120px',
                  height: '40px',
                  transform: 'translateY(-60px) rotateX(90deg)'
                }}
              />
              {/* Bottom ellipse */}
              <div
                className="absolute bg-amber-500/80 border-2 border-amber-700 rounded-full"
                style={{
                  width: '120px',
                  height: '40px',
                  transform: 'translateY(60px) rotateX(90deg)'
                }}
              />
              {/* Lateral surface */}
              <div
                className="absolute bg-gradient-to-r from-amber-400/70 via-amber-500/80 to-amber-400/70 border-x-2 border-amber-600"
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%/10%'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-amber-900">Lateral Surface</div>
              </div>
            </div>
          </div>
        );

      case 'cone':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Base circle */}
              <div
                className="absolute bg-orange-400/80 border-2 border-orange-600 rounded-full"
                style={{
                  width: '120px',
                  height: '40px',
                  transform: 'translateY(60px) rotateX(90deg)'
                }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-orange-900">Base</div>
              </div>
              {/* Cone body */}
              <div
                className="absolute"
                style={{
                  width: '0',
                  height: '0',
                  borderLeft: '60px solid transparent',
                  borderRight: '60px solid transparent',
                  borderBottom: '140px solid rgba(251, 146, 60, 0.8)',
                  transform: 'translateY(-10px)'
                }}
              />
              {/* Apex point */}
              <div
                className="absolute w-3 h-3 bg-orange-600 rounded-full"
                style={{
                  transform: 'translateY(-80px)'
                }}
              />
            </div>
          </div>
        );

      case 'sphere':
        return (
          <div className="relative w-64 h-64 mx-auto" style={{ perspective: '800px' }}>
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{
                transform: `rotateX(15deg) rotateY(${rotation}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Sphere with gradient */}
              <div
                className="absolute rounded-full bg-gradient-to-br from-emerald-300 via-emerald-400 to-emerald-600 border-4 border-emerald-700 shadow-2xl"
                style={{
                  width: '140px',
                  height: '140px'
                }}
              >
                {/* Highlight */}
                <div
                  className="absolute top-8 left-8 w-10 h-10 bg-white/40 rounded-full blur-sm"
                />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-semibold text-emerald-900">Sphere</div>
              </div>
              {/* Equator line */}
              <div
                className="absolute border-2 border-emerald-800/50 rounded-full"
                style={{
                  width: '140px',
                  height: '40px',
                  transform: 'rotateX(90deg)'
                }}
              />
              {/* Meridian line */}
              <div
                className="absolute border-2 border-emerald-800/50 rounded-full"
                style={{
                  width: '40px',
                  height: '140px'
                }}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-8 border-2 border-slate-200">
      <h3 className="text-lg font-bold text-slate-800 mb-6 text-center">{name} - 3D View</h3>
      {renderSolid()}
      <div className="mt-8 flex items-center justify-center gap-4">
        <button
          onClick={() => setRotation(rotation - 45)}
          className="px-4 py-2 bg-slate-700 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-arrow-left-line"></i>
          Rotate Left
        </button>
        <button
          onClick={() => setRotation(0)}
          className="px-4 py-2 bg-slate-600 text-white text-sm font-semibold rounded-lg hover:bg-slate-700 transition-colors whitespace-nowrap"
        >
          Reset
        </button>
        <button
          onClick={() => setRotation(rotation + 45)}
          className="px-4 py-2 bg-slate-700 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap flex items-center gap-2"
        >
          Rotate Right
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
      <p className="text-xs text-slate-600 text-center mt-4">Click the buttons to rotate the 3D model</p>
    </div>
  );
}